<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import PriceSummary from './components/PriceSummary.vue'
  import PaymentMethodList from './components/PaymentMethodList.vue'
  import CheckoutAcceptance from './components/CheckoutAcceptance.vue'
  import CheckoutActions from './components/CheckoutActions.vue'
  import PaymentTermsNotice from './components/PaymentTermsNotice.vue'
  import SslProtectionReminder from '../inclusive/SslProtectionReminder.vue'
  import { PaymentType } from '@/shared/types/response.types'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'

  interface Props {
    termsHref: string
    personCount: number
    quoteInclusivePrice: number
    quoteTotalPrice: number
    quoteNumber: string
    availablePaymentMethods: PaymentType[]
    isInboundAddon?: boolean
    inboundAddonPrice?: number
    isInboundAddonSelected?: boolean
    loadingPrice?: boolean
    isLoading?: boolean
    getApplePayTotalCallback?: () => number
    validateBeforePayment?: () => Promise<boolean>
  }

  const props = withDefaults(defineProps<Props>(), {
    termsHref: '',
    personCount: 0,
    quoteInclusivePrice: 0,
    quoteTotalPrice: 0,
    quoteNumber: '',
    isInboundAddon: false,
    inboundAddonPrice: 0,
    isInboundAddonSelected: false,
    loadingPrice: false,
    isLoading: false,
    availablePaymentMethods: () => [],
    getApplePayTotalCallback: () => 0,
    validateBeforePayment: () => Promise.resolve(true),
  })

  const emit = defineEmits<{
    (e: 'onPayment', value: { paymentMethod: number; isGpayOrApplePay: boolean }): void
    (e: 'error', value: string): void
  }>()

  const gaService = useGoogleAnalytics()

  const isLoading = ref(props.isLoading)
  const paymentMethod = ref(0)
  const acceptance = ref(true)
  const acceptanceError = ref(false)
  const googlePayAvailable = ref(false)

  const isGpayOrApplePay = computed(() => [2, 3].includes(paymentMethod.value))
  const isDibsyPayment = computed(() => [0, 2, 3].includes(paymentMethod.value))

  const onSubmit = () => {
    if (!acceptance.value && !isGpayOrApplePay.value) {
      acceptanceError.value = true
      return
    }
    emit('onPayment', {
      paymentMethod: paymentMethod.value,
      isGpayOrApplePay: isGpayOrApplePay.value,
    })
  }

  const showLoader = () => {
    isLoading.value = true
  }

  const hideLoader = () => {
    isLoading.value = false
  }

  const onAcceptanceError = () => {
    acceptanceError.value = true
  }

  const onClickTermsLink = () => {
    gaService.sendQuoteClickLink(props.quoteNumber)
  }

  watch(acceptance, () => {
    acceptanceError.value = false
  })

  watch(
    () => props.isLoading,
    (newVal) => {
      isLoading.value = newVal
    }
  )
</script>

<template>
  <PriceSummary
    :person-count="personCount"
    :quote-inclusive-price="quoteInclusivePrice"
    :quote-total-price="quoteTotalPrice"
    :inbound-addon-price="inboundAddonPrice"
    :is-inbound-addon="isInboundAddon"
    :is-inbound-addon-selected="isInboundAddonSelected"
    :loading-price="loadingPrice"
  />
  <PaymentMethodList
    v-model="paymentMethod"
    :available-payment-methods="availablePaymentMethods"
    :google-pay-available="googlePayAvailable"
  />
  <div class="quote__footer">
    <CheckoutAcceptance
      v-model="acceptance"
      :terms-href="termsHref"
      :acceptance-error="acceptanceError"
      @click-link="onClickTermsLink"
    />
    <CheckoutActions
      :payment-method="paymentMethod"
      :is-loading="isLoading"
      :loading-price="loadingPrice"
      :quote-total-price="quoteTotalPrice"
      :quote-number="quoteNumber"
      :acceptance="acceptance"
      :validate-before-payment="validateBeforePayment"
      :get-apple-pay-total-callback="getApplePayTotalCallback"
      :is-gpay-or-apple-pay="isGpayOrApplePay"
      @submit="onSubmit"
      @start-pay="showLoader"
      @cancel="hideLoader"
      @acceptance-error="onAcceptanceError"
      @gpay-enabled="googlePayAvailable = true"
      @error="emit('error', $event)"
    />
    <PaymentTermsNotice :is-dibsy-payment="isDibsyPayment" />
    <SslProtectionReminder />
  </div>
</template>

<style lang="scss">
  .without-discount {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    text-decoration-line: line-through;
    color: rgba(82, 108, 130, 0.8);
    margin: 0 12px;
    @media (max-width: 550px) {
      font-size: 14px;
    }
  }
  .discount {
    span.name,
    span.price {
      color: #18b97d;
    }
  }
  .footer-text {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    color: #1c1c1c;
    margin-top: 24px;
  }
  apple-pay-button {
    --apple-pay-button-max-width: 416px;
    --apple-pay-button-width: 100%;
    --apple-pay-button-height: 80px;
    --apple-pay-button-border-radius: 12px;
    --apple-pay-button-padding: 15px 0px;
    --apple-pay-button-box-sizing: border-box;
  }

  .per {
    &__name {
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
    }
    &__price {
      display: flex;
      font-weight: 400;
      font-size: 14px;
      line-height: 24px;
    }
    &__person-count {
      color: #75899b;
      margin-inline-end: 12px;
    }
  }

  // Moved descendants (R4 mitigation) — these wrap children of QuoteCheckout
  // that render across CheckoutAcceptance + CheckoutActions, so they cannot
  // live in `<style scoped>` here.
  .quote__footer {
    display: flex;
    flex-direction: column;
    padding-top: calc(var(--spacing-8) - var(--spacing-5));
    gap: var(--spacing-5);
  }

  .quote__terms {
    display: flex;
    align-items: center;
    padding-top: var(--spacing-3);
  }

  .quote__acceptance {
    text-align: center;
    color: var(--colorTxtSrfSecondary);
  }

  .quote__payment-loader {
    margin: 0 auto;
  }
</style>
